import { useAppSelector } from '../store/hooks';
import { useNavigate } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { useFeatureFlags } from '../hooks/useFeatureFlags';
import {
  Container,
  Typography,
  Paper,
  Box,
  Grid,
  Card,
  CardContent,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  Chip,
  Divider,
} from '@mui/material';
import {
  People,
  EventNote,
  EmojiEvents,
  School,
  FitnessCenter,
  TrendingUp,
  Groups,
  CalendarToday,
} from '@mui/icons-material';
import { dashboardAPI, DashboardStats } from '../api/dashboard.api';
import { format } from 'date-fns';

export const DashboardPage = () => {
  const { user } = useAppSelector((state) => state.auth);
  const navigate = useNavigate();
  const { features } = useFeatureFlags();
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [upcomingSessions, setUpcomingSessions] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Add a small delay to ensure auth state is fully loaded
    const timer = setTimeout(() => {
      if (user?.id) {
        fetchDashboardData();
      }
    }, 200);
    return () => clearTimeout(timer);
  }, [user?.id]);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      console.log('Fetching dashboard data...');
      const [statsData, sessionsData] = await Promise.all([
        dashboardAPI.getDashboardStats(),
        dashboardAPI.getUpcomingSessions(5),
      ]);
      console.log('Dashboard data fetched:', { statsData, sessionsData });
      setStats(statsData);
      setUpcomingSessions(sessionsData.sessions || []);
    } catch (err: any) {
      console.error('Error fetching dashboard data:', err);
      console.error('Error details:', {
        message: err.message,
        code: err.code,
        response: err.response?.data,
      });
    } finally {
      setLoading(false);
    }
  };

  const renderStatsCards = () => {
    if (!stats) return null;

    const role = user?.role;
    let statsCards: any[] = [];

    if (role === 'ADMIN' || role === 'HEAD_COACH') {
      statsCards = [
        { title: 'Total Users', value: stats.totalUsers, icon: <People />, color: '#1976d2' },
        { title: 'Total Players', value: stats.totalPlayers, icon: <Groups />, color: '#2e7d32' },
        { title: 'Total Squads', value: stats.totalSquads, icon: <EventNote />, color: '#ed6c02' },
        { title: 'Active Squads', value: stats.activeSquads, icon: <TrendingUp />, color: '#9c27b0' },
        { title: 'Upcoming Sessions', value: stats.upcomingSessions, icon: <CalendarToday />, color: '#0288d1' },
        { title: 'Total Competitions', value: stats.totalCompetitions, icon: <EmojiEvents />, color: '#d32f2f' },
      ];
    } else if (role === 'COACH') {
      statsCards = [
        { title: 'My Squads', value: stats.totalSquads, icon: <EventNote />, color: '#1976d2' },
        { title: 'Total Players', value: stats.totalPlayers, icon: <Groups />, color: '#2e7d32' },
        { title: 'Total Sessions', value: stats.totalSessions, icon: <CalendarToday />, color: '#ed6c02' },
        { title: 'Upcoming Sessions', value: stats.upcomingSessions, icon: <TrendingUp />, color: '#9c27b0' },
      ];
    } else if (role === 'PLAYER') {
      statsCards = [
        { title: 'My Squads', value: stats.totalSquads, icon: <EventNote />, color: '#1976d2' },
        { title: 'Upcoming Sessions', value: stats.upcomingSessions, icon: <CalendarToday />, color: '#2e7d32' },
        { title: 'Attendance Rate', value: stats.attendanceRate, icon: <TrendingUp />, color: '#ed6c02' },
        { title: 'Total Attendance', value: stats.totalAttendance, icon: <Groups />, color: '#9c27b0' },
      ];
    } else if (role === 'PARENT') {
      statsCards = [
        { title: 'My Children', value: stats.totalChildren, icon: <People />, color: '#1976d2' },
        { title: 'Squads Enrolled', value: stats.totalSquads, icon: <EventNote />, color: '#2e7d32' },
        { title: 'Upcoming Sessions', value: stats.upcomingSessions, icon: <CalendarToday />, color: '#ed6c02' },
      ];
    }

    return statsCards;
  };

  const allDashboardCards = [
    {
      title: 'GKM Squads',
      icon: <People sx={{ fontSize: 48 }} />,
      color: '#1a1a1a',
      bgColor: '#f5f5f5',
      link: '/squads',
      description: 'View and manage your GKM squads',
      enabled: true,
    },
    {
      title: 'My Squads',
      icon: <EventNote sx={{ fontSize: 48 }} />,
      color: '#1a1a1a',
      bgColor: '#e0e0e0',
      link: '/sessions',
      description: 'View your squad sessions',
      enabled: true,
    },
    {
      title: 'Competitions',
      icon: <EmojiEvents sx={{ fontSize: 48 }} />,
      color: '#ffc107',
      bgColor: '#fff8e1',
      link: '/competitions',
      description: 'Track your tournaments',
      enabled: features.competitions,
    },
    {
      title: 'Knowledge',
      icon: <School sx={{ fontSize: 48 }} />,
      color: '#1a1a1a',
      bgColor: '#eeeeee',
      link: '/knowledge',
      description: 'Learning resources & guides',
      enabled: features.knowledge,
    },
    {
      title: 'S&C',
      icon: <FitnessCenter sx={{ fontSize: 48 }} />,
      color: '#1a1a1a',
      bgColor: '#e8e8e8',
      link: '/snc',
      description: 'Strength & conditioning',
      enabled: features.snc,
    },
  ];

  const dashboardCards = allDashboardCards.filter(card => card.enabled !== false);

  return (
    <Container maxWidth="lg" sx={{ mt: 2, mb: 4 }}>
      <Box
        sx={{
          background: 'linear-gradient(135deg, #000000 0%, #1a1a1a 100%)',
          borderRadius: 3,
          p: 4,
          mb: 4,
          color: 'white',
          position: 'relative',
          overflow: 'hidden',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
        }}
      >
        <Box sx={{ position: 'relative', zIndex: 1 }}>
          <Typography variant="h3" gutterBottom fontWeight={700}>
            Welcome back, {user?.firstName}! 🎾
          </Typography>
          <Typography variant="h6" sx={{ opacity: 0.95, fontWeight: 400 }}>
            {user?.role.replace('_', ' ')} Dashboard
          </Typography>
        </Box>
        <Box
          sx={{
            position: 'absolute',
            right: -20,
            bottom: -20,
            fontSize: '200px',
            opacity: 0.1,
          }}
        >
          🎾
        </Box>
      </Box>

      <Grid container spacing={3}>
        {dashboardCards.map((card) => (
          <Grid item xs={12} sm={6} md={4} key={card.title}>
            <Card
              onClick={() => navigate(card.link)}
              sx={{
                cursor: 'pointer',
                height: '100%',
                transition: 'all 0.3s ease-in-out',
                position: 'relative',
                overflow: 'hidden',
                '&:hover': {
                  transform: 'translateY(-8px)',
                  boxShadow: '0 12px 24px rgba(0, 0, 0, 0.15)',
                  '& .card-bg': {
                    transform: 'scale(1.1)',
                  },
                },
              }}
            >
              <Box
                className="card-bg"
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  bgcolor: card.bgColor,
                  transition: 'transform 0.3s ease-in-out',
                }}
              />
              <CardContent sx={{ position: 'relative', zIndex: 1 }}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexDirection: 'column',
                    minHeight: 180,
                    textAlign: 'center',
                  }}
                >
                  <Box
                    sx={{
                      color: card.color,
                      mb: 2,
                      p: 2,
                      borderRadius: '50%',
                      bgcolor: 'white',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    {card.icon}
                  </Box>
                  <Typography variant="h5" fontWeight={700} sx={{ mb: 1, color: card.color }}>
                    {card.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {card.description}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Stats Cards */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <Grid container spacing={3} sx={{ mb: 4 }}>
            {renderStatsCards()?.map((stat, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <Card sx={{ height: '100%' }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <Box>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          {stat.title}
                        </Typography>
                        <Typography variant="h4" fontWeight={700}>
                          {stat.value}
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          color: stat.color,
                          bgcolor: `${stat.color}15`,
                          p: 2,
                          borderRadius: 2,
                        }}
                      >
                        {stat.icon}
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>

          {/* Upcoming Sessions */}
          <Paper sx={{ p: 3, borderRadius: 3 }}>
            <Typography variant="h5" gutterBottom fontWeight={600} color="primary">
              Upcoming Sessions
            </Typography>
            {upcomingSessions.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>
                <Typography variant="body1">No upcoming sessions scheduled</Typography>
              </Box>
            ) : (
              <List>
                {upcomingSessions.map((session, index) => (
                  <Box key={session.id}>
                    <ListItem
                      sx={{
                        cursor: 'pointer',
                        '&:hover': { bgcolor: 'action.hover' },
                        borderRadius: 1,
                      }}
                      onClick={() => navigate(`/sessions/${session.id}`)}
                    >
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="subtitle1" fontWeight={600}>
                              {session.title}
                            </Typography>
                            <Chip
                              label={session.squad?.name}
                              size="small"
                              sx={{
                                bgcolor: session.squad?.color || '#1a1a1a',
                                color: 'white',
                              }}
                            />
                          </Box>
                        }
                        secondary={
                          <Box sx={{ mt: 0.5 }}>
                            <Typography variant="body2" color="text.secondary">
                              {format(new Date(session.startTime), 'PPP p')}
                            </Typography>
                            {session.location && (
                              <Typography variant="caption" color="text.secondary">
                                📍 {session.location}
                              </Typography>
                            )}
                          </Box>
                        }
                      />
                    </ListItem>
                    {index < upcomingSessions.length - 1 && <Divider />}
                  </Box>
                ))}
              </List>
            )}
          </Paper>
        </>
      )}
    </Container>
  );
};
